import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
} from '@tanstack/react-query';

import type {
  CreateAreaTagInput,
  CreateHotspotInput,
  DeleteAreaTagInput,
  DeleteHotspotInput,
  SaveMarkersInput,
  UpdateAreaTagInput,
  UpdateHotspotInput,
  ViewerCommandAdapter,
  ViewerDataAdapter,
} from './contracts/adapter';
import { ViewerAdapterError } from './contracts/errors';
import type { AreaTag, PanoramaHotspot } from './contracts/models';

const requireCommand = <K extends keyof ViewerCommandAdapter>(
  adapter: ViewerDataAdapter,
  commandName: K,
): ViewerCommandAdapter[K] => {
  const command = adapter.commands?.[commandName];
  if (!command) {
    throw new ViewerAdapterError(
      `Viewer adapter "${adapter.id}" does not implement ${commandName}`,
      { code: 'unsupported' },
    );
  }
  return command as ViewerCommandAdapter[K];
};

export const viewerMutationOptions = {
  saveMarkers: (adapter: ViewerDataAdapter) => ({
    mutationFn: (input: SaveMarkersInput) =>
      requireCommand(adapter, 'saveMarkers')(input),
  }),
  createAreaTag: (adapter: ViewerDataAdapter) => ({
    mutationFn: (input: CreateAreaTagInput) =>
      requireCommand(adapter, 'createAreaTag')(input),
  }),
  updateAreaTag: (adapter: ViewerDataAdapter) => ({
    mutationFn: (input: UpdateAreaTagInput) =>
      requireCommand(adapter, 'updateAreaTag')(input),
  }),
  deleteAreaTag: (adapter: ViewerDataAdapter) => ({
    mutationFn: (input: DeleteAreaTagInput) =>
      requireCommand(adapter, 'deleteAreaTag')(input),
  }),
  createHotspot: (adapter: ViewerDataAdapter) => ({
    mutationFn: (input: CreateHotspotInput) =>
      requireCommand(adapter, 'createHotspot')(input),
  }),
  updateHotspot: (adapter: ViewerDataAdapter) => ({
    mutationFn: (input: UpdateHotspotInput) =>
      requireCommand(adapter, 'updateHotspot')(input),
  }),
  deleteHotspot: (adapter: ViewerDataAdapter) => ({
    mutationFn: (input: DeleteHotspotInput) =>
      requireCommand(adapter, 'deleteHotspot')(input),
  }),
};

export const useViewerSaveMarkers = (
  adapter: ViewerDataAdapter,
  options: Omit<
    UseMutationOptions<void, Error, SaveMarkersInput>,
    'mutationFn'
  > = {},
): UseMutationResult<void, Error, SaveMarkersInput> =>
  useMutation({
    ...viewerMutationOptions.saveMarkers(adapter),
    ...options,
  });

export const useViewerCreateAreaTag = (
  adapter: ViewerDataAdapter,
  options: Omit<
    UseMutationOptions<AreaTag, Error, CreateAreaTagInput>,
    'mutationFn'
  > = {},
): UseMutationResult<AreaTag, Error, CreateAreaTagInput> =>
  useMutation({
    ...viewerMutationOptions.createAreaTag(adapter),
    ...options,
  });

export const useViewerUpdateAreaTag = (
  adapter: ViewerDataAdapter,
  options: Omit<
    UseMutationOptions<AreaTag, Error, UpdateAreaTagInput>,
    'mutationFn'
  > = {},
): UseMutationResult<AreaTag, Error, UpdateAreaTagInput> =>
  useMutation({
    ...viewerMutationOptions.updateAreaTag(adapter),
    ...options,
  });

export const useViewerDeleteAreaTag = (
  adapter: ViewerDataAdapter,
  options: Omit<
    UseMutationOptions<void, Error, DeleteAreaTagInput>,
    'mutationFn'
  > = {},
): UseMutationResult<void, Error, DeleteAreaTagInput> =>
  useMutation({
    ...viewerMutationOptions.deleteAreaTag(adapter),
    ...options,
  });

export const useViewerCreateHotspot = (
  adapter: ViewerDataAdapter,
  options: Omit<
    UseMutationOptions<PanoramaHotspot, Error, CreateHotspotInput>,
    'mutationFn'
  > = {},
): UseMutationResult<PanoramaHotspot, Error, CreateHotspotInput> =>
  useMutation({
    ...viewerMutationOptions.createHotspot(adapter),
    ...options,
  });

export const useViewerUpdateHotspot = (
  adapter: ViewerDataAdapter,
  options: Omit<
    UseMutationOptions<PanoramaHotspot, Error, UpdateHotspotInput>,
    'mutationFn'
  > = {},
): UseMutationResult<PanoramaHotspot, Error, UpdateHotspotInput> =>
  useMutation({
    ...viewerMutationOptions.updateHotspot(adapter),
    ...options,
  });

export const useViewerDeleteHotspot = (
  adapter: ViewerDataAdapter,
  options: Omit<
    UseMutationOptions<void, Error, DeleteHotspotInput>,
    'mutationFn'
  > = {},
): UseMutationResult<void, Error, DeleteHotspotInput> =>
  useMutation({
    ...viewerMutationOptions.deleteHotspot(adapter),
    ...options,
  });
